import React, { useCallback, useMemo, useState } from "react";
import { CustomSelect } from "../forms/CustomSelect/CustomSelect";
import {
    CustomSelectDefaultTrigger,
    CustomSelectDefaultTriggerProps,
} from "../forms/CustomSelect/CustomSelectDefaultTrigger";
import { Account } from "../../common/types/domains/accounting/account";
import { useAccounts } from "../../hooks/useAccounts";
import styles from "./AccountSelect.module.scss";

interface Props {
    selectedAccount?: Account;
    onChange: (account: Account | null) => void;
    dropdownKey: string;
    size?: CustomSelectDefaultTriggerProps["size"];
    className?: string;
    children?: React.ReactNode;
    forTransactionEditing?: boolean;
    entityId: number;
}

export const AccountSelect: React.FC<Props> = ({
    selectedAccount,
    onChange,
    size,
    className,
    children,
    forTransactionEditing,
    entityId,
    ...customSelectProps
}) => {
    const [search, setSearch] = useState<string>();
    const [popoverVisible, setPopoverVisible] = useState(false);

    const { accounts, isLoading } = useAccounts({
        entityId,
        excludeLinkedToFinancialAccount: true,
    });

    const options = useMemo(
        () =>
            accounts
                ?.filter((c) =>
                    c.name.toLowerCase().includes(search?.toLowerCase() ?? ""),
                )
                .map((c) => ({
                    value: c.id ?? "",
                    label: c.name ?? "",
                })) ?? [],
        [accounts, search],
    );

    const onSelected = useCallback(
        (accountId: string) => {
            const account = accounts?.find((c) => c.id === accountId);
            if (account) {
                onChange(account);
            }
        },
        [onChange, accounts],
    );

    const onShowPopover = useCallback(() => {
        setPopoverVisible(true);
    }, []);

    const onHidePopover = useCallback(() => {
        setSearch(undefined);
        setPopoverVisible(false);
    }, []);

    return (
        <CustomSelect
            onSelected={onSelected}
            options={options}
            loadingOptions={isLoading}
            onSearch={setSearch}
            searchable
            externalSearch
            value={selectedAccount?.id}
            placeholder={`Select account...`}
            onShowPopover={onShowPopover}
            onHidePopover={onHidePopover}
            className={className}
            dropdownClassName={styles.dropdown}
            showPopover={popoverVisible}
            onDeselect={() => onChange(null)}
            {...customSelectProps}
        >
            {children ?? (
                <CustomSelectDefaultTrigger
                    value={selectedAccount?.name ?? `Select account...`}
                    size={size}
                />
            )}
        </CustomSelect>
    );
};
